/**
 *
 * Login
 *
 */

import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import qs from 'qs';
import { Helmet } from 'react-helmet-async';
import { createStructuredSelector } from 'reselect';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import LoadingOverlay from 'components/LoadingOverlay';
import { hideAuthCTAModal, signUp } from 'containers/Auth/actions';
import { toast } from 'react-toastify';
import {
  makeSelectProcessing,
  makeSelectSubscriptionProcessing,
  makeSelectAuth,
  makeSelectProfile,
  makeSelectError,
} from 'containers/Auth/selectors';
import { makeSelectClientDetails } from 'containers/Main/selectors';
import { getRequiredExtraData } from 'components/SAMLButton/utils';
import LoginForm from './LoginForm';
import SubscriptionForm from './SubscriptionForm';

const stateSelector = createStructuredSelector({
  auth: makeSelectAuth(),
  error: makeSelectError(),
  processing: makeSelectProcessing(),
  subscriptionProcessing: makeSelectSubscriptionProcessing(),
  profile: makeSelectProfile(),
  clientDetails: makeSelectClientDetails(),
});

const Login = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const {
    auth,
    error,
    processing,
    subscriptionProcessing,
    profile,
    clientDetails,
  } = useSelector(stateSelector);

  useEffect(() => {
    if (auth.isLoaded && !auth.isEmpty && profile.email) history.replace('/');
  }, []);

  useEffect(() => {
    dispatch(hideAuthCTAModal());
  }, []);

  useEffect(() => {
    if (
      auth.isLoaded &&
      !auth.isEmpty &&
      profile.isLoaded &&
      !profile.isEmpty
    ) {
      const isSignUp = Date.now() - auth.createdAt < 5 * 60 * 1000;

      const { redirect } = qs.parse(location.search.slice(1));
      const nextLocation = _isEmpty(redirect) ? '/' : redirect;
      if (
        !isSignUp ||
        (_get(profile, 'email') && getRequiredExtraData(profile).length === 0)
      ) {
        history.push(nextLocation);
      } else if (isSignUp && !_get(profile, 'email')) {
        history.push(`/signup/email?redirect=${nextLocation}`);
      } else if (
        isSignUp &&
        _get(profile, 'providerData.providerId') !== 'password' &&
        getRequiredExtraData(profile).length > 0
      ) {
        history.push(`/signup/data?redirect=${nextLocation}`);
      }
    }
  }, [auth, clientDetails, dispatch, history, location, profile]);

  useEffect(() => {
    if (!_isEmpty(error)) {
      toast(error.message, { type: 'error' });
    }
  }, [error]);

  const handleOAuthSignUp = provider => () => {
    dispatch(
      signUp({
        provider: { provider, type: 'popup' },
      }),
    );
  };

  const handleLogin = values => {
    dispatch(
      signUp({
        provider: values,
      }),
    );
  };

  return (
    <div>
      <Helmet>
        <title>CredibleMind | Sign In</title>
        <meta name="description" content="Sign In" />
        <link rel="canonical" href={`${window.location.origin}/login`} />
      </Helmet>
      <LoadingOverlay
        show={processing || subscriptionProcessing || !auth.isLoaded}
      />
      {!profile.isEmpty &&
      profile.email &&
      !_get(clientDetails, 'metadata.hideSubscription') &&
      !_get(clientDetails, 'metadata.requirePersonalEmail') ? (
        <SubscriptionForm />
      ) : (
        <LoginForm
          onSubmit={handleLogin}
          onGoogleLogin={handleOAuthSignUp('google')}
          onMicrosoftLogin={handleOAuthSignUp('microsoft.com')}
          onFacebookLogin={handleOAuthSignUp('facebook')}
        />
      )}
    </div>
  );
};

export default Login;
